<template>
  <ve-table v-if="tableData?.length"
            id="rta-table"
            :table-data="copyData"
            :columns="computedHeaders"
            :column-hidden-option="tableOptions.columnHiddenOption"
            :column-width-resize-option="tableOptions.columnWidthResizeOption"
            :checkbox-option="tableOptions.checkboxOption"
            :sort-option="tableOptions.sortOption"
            :virtual-scroll-option="tableOptions.virtualScrollOption"
            :event-custom-option="tableOptions.eventCustomOption"
            :fixed-header="true"
            :border-x="true"
            :border-y="true"
            :max-height="maxHeight"
            row-key-field-name="id"
            style="min-width:100%; word-break:break-word"
            :cell-style-option="cellStyleOption"
            :row-style-option="rowStyleOption" />
  <span v-else>Sem dados a serem exibidos.</span>
</template>

<script>
export default {
  name: "TableRTA",
  props: ["tableColumns", "tableData", "dbClickFunc", "priorizeFunc", "justifyFunc", "hideColsArr"],
  data() {
    return {
      maxHeight: '25rem',
      filtersMountedObj: {},
      copyData: [],
      columnFilters: {}, 
      tableOptions: {
        columnHiddenOption: { defaultHiddenColumnKeys: this.hideColsArr },
        columnWidthResizeOption: { enable: true, minWidth: "8%" },
        sortOption: {
          multipleSort: true,
          sortChange: (params) => {
            Object.keys(params).forEach(key => {
              this.tableData.sort((a, b) => {
                if (params[key] === "asc") {
                  return ('' + a[key]).localeCompare(b[key], "br", { ignorePunctuation: true });
                } else if (params[key] === "desc") {
                  return ('' + b[key]).localeCompare(a[key], "br", { ignorePunctuation: true });
                } else {
                  return 0;
                }
              });
            });
          }
        },
        virtualScrollOption: { enable: true },
        checkboxOption: {
          selectedRowChange: ({ selectedRowKeys }) => { this.occurrences.selectedRows = selectedRowKeys; },
          selectedAllChange: ({ selectedRowKeys }) => { this.occurrences.selectedRows = selectedRowKeys; },
        },
        eventCustomOption: {
          bodyRowEvents: ({ row }) => ({
            dblclick: () => { if (this.dbClickFunc) this.dbClickFunc(row); }
          }),
          bodyCellEvents: ({ row, column }) => ({
            click: () => {
              if (row.status == "Pendente" && column?.field === "priorizerta") {
                if (this.priorizeFunc) this.priorizeFunc(row);
              } else if (row.status != "Aberto" && column?.field === "justifyrta") {
                if (this.justifyFunc) this.justifyFunc(row);
              }
            }
          }),
        }
      },
      cellStyleOption: {
        headerCellClass: ({ rowIndex }) => (rowIndex === 0 ? "table-header-cell-class" : ""),
        bodyCellClass({ row, column }) {
          if (column.field === "priorizerta") return row.status == "Pendente" ? "priorizar-btn" : "priorizar-label";
          if (column.field === "justifyrta") return row.status == "Pendente" ? "justificar-btn" : "justificar-label";
          if (column.field === "efficiencyrta") {
            if (row.efficiencyrta === "Reprovado") return "ineficaz-class";
            if (row.efficiencyrta === "Aprovado") return "eficaz-class";
            return "pending-class";
          }
          if (column.field === "efficiencyaction") {
            if (row.efficiencyaction === "Reprovado") return "ineficaz-class";
            if (row.efficiencyaction === "Aprovado") return "eficaz-class";
            return "pending-class";
          }
        },
      },
      rowStyleOption: { stripe: true, hoverHighlight: true },
    };
  },

  watch: {
    tableData: {
      handler(newVal) {
        this.copyData = [...newVal];
        this.columnFilters = {};        
        this.createFiltersFunction();
      },
      deep: true,
      immediate: true
    },
    copyData: {
      handler() { this.createFiltersFunction(); },
      deep: true,
      immediate: true
    }
  },

  computed: {
    computedHeaders() {
      return this.tableColumns.map(col => ({
        ...col,
        filter: {
          isMultiple: true,
          filterList: this.filtersMountedObj[col.field] || [],
           filteredValue: this.columnFilters[col.field] || [],   
            filterConfirm: (filterList) => {
                const labels = filterList.filter(o => o.selected).map(o => o.label);
                this.$set(this.columnFilters, col.field, labels);
                this.applyColumnFilters();
            },
            filterReset: () => {
                this.$delete(this.columnFilters, col.field);
                this.applyColumnFilters();
            },
        }
      }));
    }
  },

  methods: {
    applyColumnFilters() {
      let dados = this.tableData.slice();
      Object.entries(this.columnFilters).forEach(([field, labels]) => {
        if (labels?.length) dados = dados.filter(r => labels.includes(r[field]));
      });
      this.copyData = dados;
      this.createFiltersFunction(); 
    },

    resetAllColumnFilters() {
      this.columnFilters = {};
      this.applyColumnFilters();
    },

    searchByNameField(labels, field) {                     
      this.copyData = this.tableData.filter(
        x => labels.length === 0 || labels.includes(x[field])
      );
    },

    createFiltersFunction() {
      const cols = this.tableColumns.map(i => i.field);
      const dataValues = {};
      for (const row of this.copyData) {
        for (const col of cols) {
          if (row[col] !== undefined) {
            const already = dataValues[col]?.some(it => it.label === row[col]);
            if (!already) {
              const selected = (this.columnFilters[col] || []).includes(String(row[col]));
              const filterObj = {
                value: dataValues[col] ? dataValues[col].length : 0,
                label: String(row[col]),
                selected
              };
              dataValues[col] = dataValues[col] ? [...dataValues[col], filterObj] : [filterObj];
            }
          }
        }
      }
      this.filtersMountedObj = dataValues;
    },

    onResize() { this.maxHeight = window.innerWidth > 1600 ? '38rem' : '25rem'; },
  },

  mounted() {
    this.copyData = [...this.tableData];
    this.createFiltersFunction();
    this.onResize();
    window.addEventListener('resize', this.onResize);
  },

  beforeDestroy() { window.removeEventListener('resize', this.onResize); }
};
</script>

<style scoped></style>
<style>
.table-header-cell-class {
    background: #004747 !important;
    color: #fff !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    padding: 0.1rem 0 !important;
    padding-left: 0.3rem !important;
    height: max-content !important;
    vertical-align: middle !important;
    text-align: left !important;
}

.table-header-cell-class span {
    display: inline-flex !important;
    align-items: center;
}

.table-header-cell-class span i:first-child {
    top: -3px !important;
}

.table-header-cell-class span i:last-child {
    top: 6.5px !important;
}

.table-header-cell-class .ve-table-filter-icon i {
    color: #c5c5c5 !important;
}

.priorizar-btn,
.justificar-btn {
    cursor: pointer;
}

.priorizar-btn span {
    background-color: #00A39F;
    border-radius: 0.2rem;
    font-weight: 600;
    color: #fff;
}

.justificar-btn span {
    background-color: #FA394D;
    border-radius: 0.2rem;
    font-weight: 600;
    color: #fff;
}

.justificar-label span {
    background-color: #fff4f6;
    border-radius: 0.2rem;
    font-weight: 600;
    color: #FA394D;
    cursor: pointer;
}

.priorizar-label span {
    background-color: #ebf8f8;
    border-radius: 0.2rem;
    font-weight: 600;
    color: #00A39F;
}

.eficaz-class span {
    font-weight: 600 !important;
    color: #207B0A !important;
    background-color: #c1f7c7 !important;
    border-radius: 0.2rem;
}

.ineficaz-class span {
    font-weight: 600 !important;
    color: #7B0A0C !important;
    background-color: #f7c1c2 !important;
    border-radius: 0.2rem;
}

.pending-class span {
    font-weight: 600 !important;
    color: #7B3F0A !important;
    background-color: #F7EFC1 !important;
    border-radius: 0.2rem;
}
</style>
